import { word } from "@modules/core/utils/i18n"
import { useEffect, useRef, useState } from "react"
import videojs from "video.js"
import type Player from "video.js/dist/types/player"
import "video.js/dist/video-js.css"
import { Text } from "@/components/ui/baseUi/text"

type PlayerOptions = typeof videojs.options

type VideoPlayerProps = {
  options: PlayerOptions
  onReady?: (arg: Player) => void
  size?: "extrasmall" | "small" | "medium" | "large"
} & React.ComponentProps<"div">

export function VideoPlayer({ options, onReady, size, className, ...props }: VideoPlayerProps) {
  const [uploadInProgress, setUploadInProgress] = useState<boolean>(false)
  const videoRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<Player | null>(null)

  useEffect(() => {
    if (!playerRef.current) {
      const videoElement = document.createElement("video-js")

      videoElement.classList.add("vjs-big-play-centered")
      videoElement.classList.add(`vjs-custom-${size ?? "medium"}-layout`)
      videoRef.current?.appendChild(videoElement)

      const player: Player = (playerRef.current = videojs(videoElement, options, () => {
        if (onReady) {
          onReady(player)
        }
      }))

      player.on("error", () => {
        const error = player.error()

        if (error) {
          console.error("Error while downloading video:", error)

          if (error.code === 4) setUploadInProgress(true)
        }
      })
    } else {
      const player = playerRef.current

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      player?.autoplay(options.autoplay as boolean)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      player?.src(options.sources)
    }
  }, [options, videoRef])

  // Dispose the Video.js player when the functional component unmounts
  useEffect(() => {
    const player = playerRef.current

    return () => {
      if (player && !player.isDisposed()) {
        player.dispose()
        playerRef.current = null
      }
    }
  }, [playerRef])

  return (
    <div data-vjs-player className={"relative " + className} {...props}>
      <div ref={videoRef} />

      {uploadInProgress && (
        <div
          className={`absolute top-0 left-0 z-2 flex h-full w-full items-center justify-center bg-black/50 ${size === "extrasmall" ? "px-4" : "px-8"}`}
        >
          <Text className="text-center text-textOnColor-body" fontStyle="BS">
            {size === "extrasmall"
              ? word("video.uploading.in_progress.short_message")
              : word("video.uploading.in_progress.message")}
          </Text>
        </div>
      )}
    </div>
  )
}
